import React, { useState, useEffect } from 'react';
import Draggable from 'react-draggable';
import { TestVariant } from '../types';
import { MousePointer2, Eye, GripVertical } from 'lucide-react';

interface FigmaEmbedProps {
  variant: TestVariant;
  onComplete: () => void;
}

export default function FigmaEmbed({ variant, onComplete }: FigmaEmbedProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isInstructionsVisible, setIsInstructionsVisible] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [iframeKey, setIframeKey] = useState(Date.now());

  useEffect(() => {
    // Reset iframe on variant change
    setIframeKey(Date.now());
    setIsLoading(true);
    setHasError(false);
  }, [variant.id]);

  const handleIframeLoad = () => {
    console.log('Figma iframe loaded successfully');
    setIsLoading(false);
  };

  const handleIframeError = () => {
    console.error('Figma iframe failed to load');
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div className="fixed inset-0 bg-gray-50">
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative w-full h-full max-w-[1600px] max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl bg-white">
          {isLoading && (
            <div className="absolute inset-0 flex justify-center items-center bg-gray-50 z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
          
          {hasError ? (
            <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
              <div className="text-red-500 mb-4">
                <div className="w-16 h-16 mx-auto border-2 border-red-500 rounded-full flex items-center justify-center">
                  <span className="text-2xl">!</span>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Unable to load Figma prototype</h3>
              <p className="text-sm text-gray-600 mb-4">Please check that the Figma file is publicly accessible.</p>
              <p className="text-xs text-gray-500 mb-4 font-mono break-all">URL: {variant.figmaUrl}</p>
              <button 
                onClick={() => {
                  setIframeKey(Date.now());
                  setIsLoading(true);
                  setHasError(false);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors mb-4"
              >
                Retry
              </button>
              <a 
                href={variant.figmaUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-600 underline text-sm"
              >
                Open in Figma →
              </a>
            </div>
          ) : (
            <iframe
              key={iframeKey}
              className="absolute inset-0 w-full h-full border-0"
              src={variant.figmaUrl}
              allowFullScreen
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          )}
        </div>
      </div>

      <Draggable
        handle=".drag-handle"
        defaultPosition={{ x: 20, y: -40 }}
        bounds="parent"
      >
        <div 
          className="absolute bottom-0 left-0 z-50 w-80 bg-white rounded-lg shadow-xl transition-all duration-300 flex flex-col"
          style={{ maxHeight: isInstructionsVisible ? '250px' : 'auto' }}
        >
          <div className={`flex-1 overflow-y-auto ${!isInstructionsVisible && 'hidden'}`}>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Task</h3>
              <p className="text-sm text-gray-600">{variant.name}</p>

              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="space-y-6">
                  <h3 className="text-medium font-semibold text-gray-800 mb-4">Instructions</h3>
                  <div className="flex items-start space-x-3">
                    <div className="shrink-0 mt-1">
                      <MousePointer2 className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-700 mb-1">Interact</h3>
                      <p className="text-sm text-gray-600">
                        Click through the prototype as you would with a real application. All blue elements are interactive.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="shrink-0 mt-1">
                      <Eye className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-700 mb-1">Explore</h3>
                      <p className="text-sm text-gray-600">
                        Take your time to explore all features and workflows. Try to complete common tasks.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg border-t border-gray-100 flex items-center gap-2">
            <GripVertical className="h-4 w-4 text-gray-400 cursor-move drag-handle" />
            <div className="flex-1 flex justify-end gap-2">
              <button
                onClick={onComplete}
                className="px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 transition-colors duration-300 flex items-center"
              >
                I'm done
              </button>
              <button
                onClick={() => setIsInstructionsVisible(!isInstructionsVisible)}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 transition-colors duration-300 flex items-center gap-2"
              >
                {isInstructionsVisible ? 'Hide instructions' : 'Show instructions'}
              </button>
            </div>
          </div>
        </div>
      </Draggable>
    </div>
  );
}